/**
 * The stored-model list in the sidebar.
 *
 * Rebuilt wholesale on every change. The list is a handful of entries at human scale, so
 * diffing it would be complexity bought with nothing.
 */

import { colourOf, describeColour } from '../domain/colour.js';
import { hasFileColours } from '../domain/modelFile.js';
import { roomScaleOf } from '../domain/nudge.js';
import { boundsSize } from '../domain/scaling.js';
import type { ModelId, ModelMeta } from '../domain/types.js';
import {
  describeContents,
  describeRoomScale,
  describeToolpathCaveat,
  formatBytes,
  formatDimensions,
} from '../format.js';

export interface LibraryPanelOptions {
  readonly list: HTMLElement;
  readonly empty: HTMLElement;
  /** Where the standing caveat about G-code goes when there is one to make. */
  readonly notice: HTMLElement;
  /** The strip of squares that stands in for the list while the sidebar is folded away. */
  readonly rail: HTMLElement;
  /**
   * A picture of a model, if one is to hand.
   *
   * Answers immediately or not at all: this is called while building the page, which must
   * not wait on storage or on a renderer. A model with no picture yet gets a square with
   * its colour in it, and the same call is what sets one being made.
   */
  readonly thumbnailFor: (meta: ModelMeta) => string | undefined;
  readonly onSelect: (id: ModelId) => void;
  readonly onDelete: (meta: ModelMeta) => void;
}

export class LibraryPanel {
  constructor(private readonly options: LibraryPanelOptions) {}

  render(models: readonly ModelMeta[], selectedId: ModelId | undefined): void {
    const { list, empty, notice, rail } = this.options;

    list.replaceChildren(
      ...models.map((meta) => this.renderItem(meta, meta.id === selectedId)),
    );
    rail.replaceChildren(
      ...models.map((meta) => this.renderSquare(meta, meta.id === selectedId)),
    );
    empty.hidden = models.length > 0;

    // Written only when it actually changes. This is a live region, so assigning the same
    // sentence again on every unrelated library change — a colour saved, a model selected
    // — would have a screen reader read the whole caveat out afresh each time.
    const caveat = describeToolpathCaveat(models) ?? '';
    if (notice.textContent !== caveat) notice.textContent = caveat;
    notice.hidden = caveat === '';
  }

  /**
   * One model as it appears in the folded-away strip: a square with a picture of it.
   *
   * The picture is the whole entry, so it carries the name — which is why the square is a
   * button with a title and an accessible name rather than an image with a caption. There
   * is no room for a caption and no room for a delete button either: the strip is for
   * getting back to a model, and the operations belong with the list that has room to
   * label them.
   *
   * Before a picture exists the square shows the model's colour, which is not a
   * placeholder for the sake of one — it is the same colour the picture will be, so the
   * square does not change identity when the picture lands, only detail.
   */
  private renderSquare(meta: ModelMeta, isSelected: boolean): HTMLLIElement {
    const item = document.createElement('li');
    item.className = 'rail__item';

    const select = document.createElement('button');
    select.type = 'button';
    select.className = isSelected ? 'rail__model rail__model--selected' : 'rail__model';
    select.title = meta.name;
    select.setAttribute('aria-label', meta.name);
    select.setAttribute('aria-pressed', String(isSelected));
    select.addEventListener('click', () => {
      this.options.onSelect(meta.id);
    });

    const ownColours = hasFileColours(meta);
    if (!ownColours) select.style.setProperty('--model-colour', colourOf(meta));
    if (ownColours) select.classList.add('rail__model--own');

    const url = this.options.thumbnailFor(meta);
    if (url !== undefined) {
      const image = document.createElement('img');
      image.className = 'rail__image';
      image.src = url;
      // The button beneath is already named for the model, so a second reading of the same
      // name would be the name twice.
      image.alt = '';
      image.decoding = 'async';
      select.append(image);
    }

    item.append(select);
    return item;
  }

  private renderItem(meta: ModelMeta, isSelected: boolean): HTMLLIElement {
    const item = document.createElement('li');
    item.className = isSelected ? 'model model--selected' : 'model';

    const select = document.createElement('button');
    select.type = 'button';
    select.className = 'model__select';
    select.setAttribute('aria-pressed', String(isSelected));
    select.addEventListener('click', () => {
      this.options.onSelect(meta.id);
    });

    const ownColours = hasFileColours(meta);
    const colour = colourOf(meta);

    // Shows what the model will be rendered in, before it is selected. Marked decorative
    // because the same fact is in the accessible name below — a bare coloured dot conveys
    // nothing to a screen reader, and colour alone should not be the only way to know.
    //
    // A model drawn from the file's own colours gets a dot of its own rather than a
    // single colour, because there is no single colour to show and picking one of them
    // would claim something about the model that is not true.
    const swatch = document.createElement('span');
    swatch.className = ownColours ? 'model__swatch model__swatch--own' : 'model__swatch';
    if (!ownColours) swatch.style.backgroundColor = colour;
    swatch.setAttribute('aria-hidden', 'true');

    const name = document.createElement('span');
    name.className = 'model__name';
    name.textContent = meta.name;
    name.title = meta.name;

    // A resize belongs on the same line as the dimensions it applies to, and has to say
    // where it applies: this list sits beside a view the resize deliberately does not
    // touch, so a bare "50%" here would be a figure contradicted by the picture next to
    // it. In the headset's own menu there is nothing to contradict and it stands alone.
    const scaled = describeRoomScale(roomScaleOf(meta));

    const detail = document.createElement('span');
    detail.className = 'model__detail';
    detail.textContent = [
      formatDimensions(boundsSize(meta.bounds)),
      ...(scaled === undefined ? [] : [`${scaled} in VR`]),
      describeContents(meta),
      formatBytes(meta.byteSize),
    ].join(' · ');

    const colourName = document.createElement('span');
    colourName.className = 'visually-hidden';
    colourName.textContent = ownColours
      ? ', shown in its own colours'
      : `, shown in ${describeColour(colour)}`;

    // The swatch shares the name's line rather than sitting beside the whole entry, which
    // leaves the detail line its full width — it is long enough to wrap awkwardly if any
    // of it is taken away.
    const heading = document.createElement('span');
    heading.className = 'model__heading';
    heading.append(swatch, name, colourName);

    select.append(heading, detail);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'model__delete';
    remove.textContent = '×';
    remove.title = `Delete ${meta.name}`;
    remove.setAttribute('aria-label', `Delete ${meta.name}`);
    remove.addEventListener('click', () => {
      this.options.onDelete(meta);
    });

    item.append(select, remove);
    return item;
  }
}
